import { formatAmount, type RuleBoxHistoryEntry } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { useInfiniteQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { amountColumnHeader, useCurrency } from "../lib/currency";
import { formatFullDateTime, formatRelativeTime } from "../lib/format";
import {
  hasRuleBoxChanges,
  RULE_BOX_ADDITION_REASON_LABELS,
  RULE_BOX_OUTCOME_LABELS,
  RULE_BOX_REMOVAL_REASON_LABELS,
  ruleBoxActorLabel,
  ruleBoxHistoryHeadline,
  ruleBoxItemCountChange,
  ruleBoxPriceChange,
  ruleBoxVersionChange,
} from "../lib/ruleBoxHistory";
import { useTRPC } from "../lib/trpc";
import { ErrorAlert } from "./ErrorAlert";
import { ExpandableRow } from "./ExpandableRow";
import { LoadMoreButton } from "./LoadMoreButton";
import { TableMessageRow } from "./TableMessageRow";

const COLUMNS = 7;

/**
 * A Shopify rule box's History tab: every refresh of its generated drop table, newest first, from
 * `shopify.boxHistory`. A row with product changes opens in place to what was added, removed
 * and revalued.
 */
export function RuleBoxHistoryCard({ boxId }: { boxId: string }) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const [expanded, setExpanded] = useState<string | null>(null);
  const query = useInfiniteQuery(
    trpc.shopify.boxHistory.infiniteQueryOptions(
      { boxId, limit: 25 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];
  const now = new Date();
  const format = (cents: number) => formatAmount(cents, currency);

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>History</CardTitle>
        <CardDescription>
          Every refresh of this box's items from its rules, newest first: the daily refresh, stock
          and product changes from Shopify, rule changes and renames. A refused refresh leaves the
          last version on sale.
        </CardDescription>
      </CardHeader>
      {/* A container, so an open row's detail can take the card's width and not the table's. */}
      <CardContent className="@container flex flex-col gap-2">
        {query.error && <ErrorAlert message={query.error.message} />}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>When</TableHead>
              <TableHead>What happened</TableHead>
              <TableHead>By</TableHead>
              <TableHead>Version</TableHead>
              <TableHead className="text-right">{amountColumnHeader("Price", currency)}</TableHead>
              <TableHead className="text-right">Items</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && <TableMessageRow colSpan={COLUMNS}>Loading…</TableMessageRow>}
            {query.isSuccess && entries.length === 0 && (
              <TableMessageRow colSpan={COLUMNS}>No refreshes yet.</TableMessageRow>
            )}
            {entries.map((entry) => {
              const cells = <HistoryCells entry={entry} now={now} format={format} />;
              if (!hasRuleBoxChanges(entry)) {
                return (
                  <TableRow key={entry.id}>
                    <TableCell className="w-8 pr-0" />
                    {cells}
                  </TableRow>
                );
              }
              return (
                <ExpandableRow
                  key={entry.id}
                  detailId={`rule-box-history-${entry.id}`}
                  label={`Show the changes of ${formatFullDateTime(entry.at)}`}
                  isOpen={expanded === entry.id}
                  onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)}
                  colSpan={COLUMNS}
                  detail={<HistoryChanges changes={entry.changes} format={format} />}
                >
                  {cells}
                </ExpandableRow>
              );
            })}
          </TableBody>
        </Table>
        <LoadMoreButton
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

/** One entry's cells after the chevron. */
function HistoryCells({
  entry,
  now,
  format,
}: {
  entry: RuleBoxHistoryEntry;
  now: Date;
  format: (cents: number) => string;
}) {
  return (
    <>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        <Tooltip>
          <TooltipTrigger render={<time dateTime={entry.at} className="cursor-default" />}>
            {formatRelativeTime(entry.at, now)}
          </TooltipTrigger>
          <TooltipContent>{formatFullDateTime(entry.at)}</TooltipContent>
        </Tooltip>
      </TableCell>
      <TableCell className="whitespace-normal">
        <div className="flex flex-wrap items-center gap-2">
          <span className={entry.outcome === "refused" ? "text-destructive" : undefined}>
            {ruleBoxHistoryHeadline(entry)}
          </span>
          {/* A rename is not a refresh, so "No change" would misread it. */}
          {entry.outcome !== "refused" && entry.trigger !== "renamed" && (
            <Badge variant={entry.outcome === "published" ? "secondary" : "outline"}>
              {RULE_BOX_OUTCOME_LABELS[entry.outcome]}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">{ruleBoxActorLabel(entry.actor)}</TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs">
        {ruleBoxVersionChange(entry)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right font-mono text-xs">
        {ruleBoxPriceChange(entry, format)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right font-mono text-xs">
        {ruleBoxItemCountChange(entry)}
      </TableCell>
    </>
  );
}

/** An open row's detail: the products added, removed and revalued, each with its reason. */
function HistoryChanges({
  changes,
  format,
}: {
  changes: RuleBoxHistoryEntry["changes"];
  format: (cents: number) => string;
}) {
  return (
    <div className="grid gap-4 py-2 text-sm @2xl:grid-cols-3">
      <ChangeList title="Added" count={changes.added.length}>
        {changes.added.map((item) => (
          <li key={item.catalogItemId}>
            <span className="font-medium">{item.name}</span>{" "}
            <span className="text-muted-foreground">
              {format(item.valueCents)} · {RULE_BOX_ADDITION_REASON_LABELS[item.reason]}
            </span>
          </li>
        ))}
      </ChangeList>
      <ChangeList title="Removed" count={changes.removed.length}>
        {changes.removed.map((item) => (
          <li key={item.catalogItemId}>
            <span className="font-medium">{item.name}</span>{" "}
            <span className="text-muted-foreground">
              {format(item.valueCents)} · {RULE_BOX_REMOVAL_REASON_LABELS[item.reason]}
            </span>
          </li>
        ))}
      </ChangeList>
      <ChangeList title="Revalued" count={changes.revalued.length}>
        {changes.revalued.map((item) => (
          <li key={item.catalogItemId}>
            <span className="font-medium">{item.name}</span>{" "}
            <span className="text-muted-foreground">
              {format(item.fromCents)} → {format(item.toCents)}
            </span>
          </li>
        ))}
      </ChangeList>
    </div>
  );
}

function ChangeList({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title} ({count})
      </h4>
      {count === 0 ? (
        <p className="text-muted-foreground">None</p>
      ) : (
        <ul className="flex flex-col gap-1">{children}</ul>
      )}
    </div>
  );
}
